import { z } from 'zod'

// ─── Auth Schemas ─────────────────────────────────────────────────────────────

export const registerSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  displayName: z.string().min(2).max(60),
})

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
})

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
})

// ─── Campus Schemas ───────────────────────────────────────────────────────────

export const campusTypeSchema = z.enum(['main', 'satellite', 'school', 'community_center'])

const serviceTimeSchema = z.object({
  name: z.string().max(60),
  day: z.string().max(20),
  time: z.string().max(20),
})

export const bulletinSettingsSchema = z.object({
  template: z.enum(['classic', 'modern', 'postcard']),
  showLogo: z.boolean(),
  showServiceTimes: z.boolean(),
  showEvents: z.boolean(),
  showGiving: z.boolean(),
  showVerse: z.boolean(),
  maxEvents: z.number().int().min(1).max(8),
  verse: z.string().max(500),
})

export const createCampusSchema = z.object({
  name: z.string().min(1).max(100),
  type: campusTypeSchema.default('satellite'),
  addressLine1: z.string().min(1).optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().default('US'),
  locationLat: z.number().min(-90).max(90).optional(),
  locationLng: z.number().min(-180).max(180).optional(),
  phone: z.string().optional(),
  serviceTimes: z.array(serviceTimeSchema).optional(),
})

export const updateCampusSchema = createCampusSchema.partial()

// ─── Church Schemas ───────────────────────────────────────────────────────────

export const createChurchSchema = z.object({
  name: z.string().min(2).max(100),
  description: z.string().max(1000).nullable().optional(),
  websiteUrl: z.string().url().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email(),
  addressLine1: z.string().min(1),
  addressLine2: z.string().nullable().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  zip: z.string().min(1),
  country: z.string().default('US'),
  locationLat: z.number().min(-90).max(90),
  locationLng: z.number().min(-180).max(180),
  denomination: z.string().default('other'),
})

export const updateChurchSchema = createChurchSchema.partial().extend({
  // Address fields are optional on update — allow null to clear them.
  // (createChurchSchema makes these required + non-empty; .partial() alone
  // only makes them optional, still rejecting the null the edit form sends.)
  tagline: z.string().max(120).nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  serviceTimes: z.array(serviceTimeSchema).optional(),
  givingUrl: z.string().url().nullable().optional(),
  bulletinSettings: bulletinSettingsSchema.nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  coverImageUrl: z.string().url().nullable().optional(),
})

// ─── Event Schemas ────────────────────────────────────────────────────────────

const dishGoalsSchema = z.object({
  main: z.number().int().min(0).optional(),
  salad: z.number().int().min(0).optional(),
  dessert: z.number().int().min(0).optional(),
  drink: z.number().int().min(0).optional(),
  side: z.number().int().min(0).optional(),
  bringPctGoal: z.number().int().min(0).max(100).optional(),
})

const eventMetadataSchema = z.object({
  dishGoals: dishGoalsSchema.optional(),
  // Hike fields
  difficulty: z.enum(['easy', 'moderate', 'strenuous']).optional(),
  distanceMiles: z.number().min(0).optional(),
  elevationFt: z.number().min(0).optional(),
  meetupNote: z.string().max(500).optional(),
  // Study fields
  topic: z.string().max(200).optional(),
  scriptureRef: z.string().max(200).optional(),
  // Sport fields
  sport: z.string().max(100).optional(),
})

// Capability flags are explicit; filter-tag slugs are admin-managed and dynamic,
// so accept any additional boolean key. The API validates slugs against the
// active event_tags set on save (unknown slugs are stripped).
export const eventAttributesSchema = z
  .object({
    bringADish: z.boolean().optional(),
    outdoorActivity: z.boolean().optional(),
  })
  .catchall(z.boolean())
  .default({})

// ── Event tags (admin-managed filter attributes) ───────────────────────────────

export const eventTagCategorySchema = z.enum(['dietary', 'audience'])

export const createEventTagSchema = z.object({
  label: z.string().min(1).max(60),
  category: eventTagCategorySchema,
  // Optional: server auto-generates a slug from the label when omitted.
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'Slug must be alphanumeric and start with a letter')
    .optional(),
  sortOrder: z.number().int().optional(),
})

export const updateEventTagSchema = z.object({
  label: z.string().min(1).max(60).optional(),
  category: eventTagCategorySchema.optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
})

export const createEventSchema = z.object({
  eventType: z.enum(['potluck', 'worship', 'study', 'groups', 'social', 'outdoors', 'kids', 'service']).default('potluck'),
  title: z.string().min(2).max(100),
  description: z.string().max(2000).optional(),
  locationName: z.string().max(100).optional().nullable(),
  addressText: z.string().min(5),
  locationLat: z.number().min(-90).max(90),
  locationLng: z.number().min(-180).max(180),
  locationPublicLat: z.number().min(-90).max(90).optional(),
  locationPublicLng: z.number().min(-180).max(180).optional(),
  locationMode: z.enum(['private', 'venue', 'pin']).optional().nullable(),
  visibility: z.enum(['public', 'unlisted', 'invite_only']).default('public'),
  audience: z.enum(['everyone', 'denomination']).default('everyone'),
  status: z.enum(['active', 'draft']).default('active'),
  rsvpRequiresApproval: z.boolean().default(false),
  hideAddressUntilApproved: z.boolean().default(false),
  maxAttendees: z.number().int().min(1).optional(),
  isRecurring: z.boolean().default(false),
  rrule: z.string().optional(),
  recurrenceEndsOn: z.string().datetime().optional(),
  tags: z.array(z.string()).max(10).default([]),
  attributes: eventAttributesSchema,
  metadata: eventMetadataSchema.optional(),
  imageUrl: z.string().url().optional().nullable(),
  startsAt: z.string().datetime(), // for one-off or first occurrence (DTSTART for recurring)
  endsAt: z.string().datetime().optional(),
  rsvpClosesAt: z.string().datetime().optional().nullable(),
  churchId: z.string().uuid().optional(), // if present, church-hosted (requires active subscription)
})

export const updateEventSchema = createEventSchema.partial()

export const searchEventsSchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  radius: z.coerce.number().min(1).max(100).default(25),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  tags: z.string().optional(), // comma-separated
  attributes: z.string().optional(), // comma-separated attribute keys e.g. "kidFriendly,noMeat"
  eventTypes: z.string().optional(), // comma-separated event type keys
  hostedByType: z.enum(['person', 'church']).optional(), // filter to person- or church-hosted
  denomination: z.string().optional(), // church denomination filter
  includePast: z.coerce.boolean().optional(),
  sort: z.enum(['closest', 'soonest', 'popular', 'newest']).default('closest'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
})

// ─── RSVP Schemas ─────────────────────────────────────────────────────────────

export const createRsvpSchema = z.object({
  occurrenceId: z.string().uuid(),
  attendanceStatus: z.enum(['going', 'maybe', 'not_going']).default('going'),
  dishCategory: z.enum(['main', 'salad', 'dessert', 'drink', 'side', 'none']).default('none'),
  dishDescription: z.string().max(200).optional(),
  guestsCount: z.number().int().min(1).max(20).default(1),
  note: z.string().max(500).optional(),
  // For non-user RSVPs via invite token
  guestName: z.string().min(1).max(60).optional(),
  guestEmail: z.string().email().optional(),
  inviteToken: z.string().optional(),
})

export const updateOccurrenceSchema = z.object({
  isCancelled: z.boolean().optional(),
  cancellationNote: z.string().max(500).optional(),
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().optional(),
  overrideTitle: z.string().max(100).optional(),
  overrideDescription: z.string().max(2000).optional(),
  overrideAddressText: z.string().optional(),
  overrideLocationLat: z.number().min(-90).max(90).optional(),
  overrideLocationLng: z.number().min(-180).max(180).optional(),
})

export const updateRsvpSchema = createRsvpSchema
  .pick({
    attendanceStatus: true,
    dishCategory: true,
    dishDescription: true,
    guestsCount: true,
    note: true,
  })
  .partial()

// ─── Super-admin schemas ──────────────────────────────────────────────────────

export const adminCreateUserSchema = registerSchema.extend({
  role: z.enum(['person', 'church_admin', 'super_admin']).default('person'),
})

export const adminUpdateUserSchema = z
  .object({
    displayName: z.string().min(2).max(60).optional(),
    email: z.string().email().optional(),
    role: z.enum(['person', 'church_admin', 'super_admin']).optional(),
    password: z.string().min(8).optional(),
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field required' })

export const adminUpdateChurchSchema = updateChurchSchema
  .extend({
    slug: z
      .string()
      .min(2)
      .max(100)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase letters, numbers, and hyphens')
      .optional(),
    verified: z.boolean().optional(),
    claimed: z.boolean().optional(),
    email: z.string().email().nullable().optional(),
    // Admin can clear phone and website by sending null
    phone: z.string().nullable().optional(),
    websiteUrl: z.string().url().nullable().optional(),
  })
  .partial()

export const adminAddChurchMemberSchema = z.object({
  userId: z.string().uuid(),
  role: z.enum(['owner', 'coordinator', 'viewer']).default('coordinator'),
})

export const adminCreateEventSchema = z.object({
  title: z.string().min(2).max(100),
  eventType: z
    .enum(['potluck', 'worship', 'study', 'groups', 'social', 'outdoors', 'kids', 'service'])
    .default('potluck'),
  description: z.string().max(2000).optional(),
  addressText: z.string().min(5),
  locationLat: z.number().min(-90).max(90),
  locationLng: z.number().min(-180).max(180),
  hostedByType: z.enum(['person', 'church']),
  hostedById: z.string().uuid(),
  visibility: z.enum(['public', 'unlisted', 'invite_only']).default('public'),
  source: z.enum(['claimed', 'scraped']).default('claimed'),
  status: z.enum(['active', 'cancelled', 'completed']).default('active'),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().optional(),
})

export const adminUpdateEventSchema = adminCreateEventSchema
  .omit({ startsAt: true, endsAt: true })
  .partial()
  .extend({
    status: z.enum(['active', 'cancelled', 'completed']).optional(),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional().nullable(),
  })

// ─── Inferred Types ───────────────────────────────────────────────────────────
// Use these as the canonical input types throughout the codebase

export type RegisterInput = z.infer<typeof registerSchema>
export type LoginInput = z.infer<typeof loginSchema>
export type CreateCampusInput = z.infer<typeof createCampusSchema>
export type UpdateCampusInput = z.infer<typeof updateCampusSchema>
export type CreateChurchInput = z.infer<typeof createChurchSchema>
export type UpdateChurchInput = z.infer<typeof updateChurchSchema>
export type CreateEventInput = z.infer<typeof createEventSchema>
export type UpdateEventInput = z.infer<typeof updateEventSchema>
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>
export type AdminUpdateChurchInput = z.infer<typeof adminUpdateChurchSchema>
export type AdminCreateEventInput = z.infer<typeof adminCreateEventSchema>
export type AdminUpdateEventInput = z.infer<typeof adminUpdateEventSchema>
export type SearchEventsInput = z.infer<typeof searchEventsSchema>
export type UpdateOccurrenceInput = z.infer<typeof updateOccurrenceSchema>

// ─── Approval Schema ──────────────────────────────────────────────────────────

export const approvalUpdateSchema = z.object({
  approvalStatus: z.enum(['approved', 'declined']),
})
export type ApprovalUpdateInput = z.infer<typeof approvalUpdateSchema>
export type CreateRsvpInput = z.infer<typeof createRsvpSchema>
export type UpdateRsvpInput = z.infer<typeof updateRsvpSchema>

// ─── Broadcast Schema ─────────────────────────────────────────────────────────

export const broadcastSchema = z.object({
  subject: z.string().min(1).max(200),
  message: z.string().min(1).max(2000),
  filter: z.enum(['all', 'going', 'going_maybe', 'no_dish']).default('all'),
})
export type BroadcastInput = z.infer<typeof broadcastSchema>

// ─── Moderation Schema ────────────────────────────────────────────────────────

export const resolveContentFlagSchema = z.object({
  action: z.enum(['confirm', 'dismiss']),
})
export type ResolveContentFlagInput = z.infer<typeof resolveContentFlagSchema>

// ─── Support / Feedback Schema ────────────────────────────────────────────────

export const supportRequestTypeSchema = z.enum(['bug', 'feature', 'question'])

export const createSupportRequestSchema = z.object({
  type: supportRequestTypeSchema,
  message: z.string().trim().min(1, 'Please enter a message').max(5000),
  // Optional contact for anonymous submitters. Coerce empty string → undefined.
  email: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().email('Invalid email address').optional(),
  ),
  pageUrl: z.string().max(2000).optional(),
})
export type CreateSupportRequestInput = z.infer<typeof createSupportRequestSchema>
